import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Badge, statusBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MileageInput from "@/components/MileageInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, ArrowUpIcon, ArrowDownIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { sortByUnit } from "@/lib/unitSort";

const SERVICE_TYPES_TRUCK = [
  "oil_change",
  "tire_rotation",
  "tire_change",
  "brake_service",
  "pm_inspection",
  "annual_safety",
  "inspection",
  "filter_change",
  "transmission_service",
  "coolant_flush",
  "other",
];

const SERVICE_TYPES_TRAILER = [
  "pm_inspection",
  "annual_safety",
  "inspection",
  "tire_change",
  "tire_rotation",
  "brake_service",
  "reefer_oil_change",
  "reefer_belt",
  "reefer_inspection",
  "reefer_compressor",
  "other",
];

// Days added to last_service_date to auto-compute next_due_date for
// recurring inspections. User can override in the form.
const AUTO_INTERVAL_DAYS = {
  annual_safety: 365,
  pm_inspection: 122, // ≈ 4 months
};

// Normalize a service_type value so near-duplicates (case/spacing/underscore
// differences) collapse to a single canonical form. e.g. "Annual Safety",
// "annual_safety" and "ANNUAL  SAFETY" all -> "annual safety".
function normKey(v) {
  return String(v || "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function addDaysISO(dateStr, days) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d)) return "";
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const empty = {
  truck_id: "",
  trailer_id: "",
  asset_kind: "truck",
  service_type: "oil_change",
  description: "",
  last_service_date: "",
  last_service_mileage: "",
  last_service_engine_hours: "",
  next_due_date: "",
  next_due_mileage: "",
  next_due_engine_hours: "",
  cost: 0,
  performed_by: "",
  status: "upcoming",
  notes: "",
};

export default function Services() {
  const [services, setServices] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  // Filters
  const [filterAsset, setFilterAsset] = useState("all"); // all | truck | trailer
  const [filterType, setFilterType] = useState("all");    // all | <service_type>
  const [sortDir, setSortDir] = useState("asc");           // sort direction asc | desc
  const [sortBy, setSortBy] = useState("unit");            // unit | date (next due)

  // Auto-fill next_due_mileage whenever service_type or truck changes IF blank.
  // Uses the service type's default_interval_mi so admins don't have to do math.
  useEffect(() => {
    if (form.next_due_mileage) return;  // never overwrite an edited value
    const type = serviceTypes.find((t) => t.key === form.service_type);
    if (!type || !type.default_interval_mi) return;
    if (form.asset_kind !== "truck" || !form.truck_id) return;
    const truck = trucks.find((t) => t.id === form.truck_id);
    if (!truck || truck.mileage == null) return;
    setForm((f) => ({ ...f, next_due_mileage: Number(truck.mileage) + Number(type.default_interval_mi) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.service_type, form.truck_id, form.asset_kind]);

  // Auto-fill next_due_date whenever service_type or last_service_date changes
  // for annual_safety / pm_inspection. User may still override.
  useEffect(() => {
    const interval = AUTO_INTERVAL_DAYS[form.service_type];
    if (!interval || !form.last_service_date) return;
    const auto = addDaysISO(form.last_service_date, interval);
    if (auto && auto !== form.next_due_date) {
      setForm((f) => ({ ...f, next_due_date: auto }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.service_type, form.last_service_date]);

  const load = async () => {
    const [s, t, tr, st] = await Promise.all([
      api.get("/services"),
      api.get("/trucks"),
      api.get("/trailers"),
      api.get("/service-types"),
    ]);
    setServices(s.data);
    setTrucks(t.data);
    setTrailers(tr.data);
    setServiceTypes(st.data || []);
  };

  useEffect(() => { load(); }, []);

  // Auto-refresh when the mechanic app creates/updates a service so the row
  // shows up on the web without a manual reload.
  useEffect(() => {
    const onChange = (ev) => {
      if (ev?.detail?.entity === "services") load();
    };
    window.addEventListener("rf-data-change", onChange);
    return () => window.removeEventListener("rf-data-change", onChange);
  }, []);

  const truckLabel = (id) => {
    const t = trucks.find((x) => x.id === id);
    return t ? `${t.unit_number} · ${t.make} ${t.model}` : "—";
  };

  const trailerLabel = (id) => {
    const t = trailers.find((x) => x.id === id);
    return t ? `${t.unit_number} · ${t.trailer_type.replace(/_/g, " ")}` : "—";
  };

  const assetLabel = (s) => s.trailer_id ? trailerLabel(s.trailer_id) : truckLabel(s.truck_id);

  const unitNumberOf = (s) => {
    const list = s.trailer_id ? trailers : trucks;
    return list.find((x) => x.id === (s.trailer_id || s.truck_id))?.unit_number || "";
  };

  const openAdd = () => { setEditing(null); setForm({ ...empty, truck_id: trucks[0]?.id || "", asset_kind: "truck" }); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      ...empty,
      ...s,
      asset_kind: s.trailer_id ? "trailer" : "truck",
      truck_id: s.truck_id || "",
      trailer_id: s.trailer_id || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        truck_id: form.asset_kind === "truck" ? (form.truck_id || null) : null,
        trailer_id: form.asset_kind === "trailer" ? (form.trailer_id || null) : null,
        cost: Number(form.cost) || 0,
        last_service_mileage: form.last_service_mileage ? Number(form.last_service_mileage) : null,
        next_due_mileage: form.next_due_mileage ? Number(form.next_due_mileage) : null,
        last_service_engine_hours: form.last_service_engine_hours ? Number(form.last_service_engine_hours) : null,
        next_due_engine_hours: form.next_due_engine_hours ? Number(form.next_due_engine_hours) : null,
        last_service_date: form.last_service_date || null,
        next_due_date: form.next_due_date || null,
      };
      delete body.asset_kind;
      if (editing) await api.put(`/services/${editing.id}`, body);
      else await api.post("/services", body);
      toast.success(editing ? "Service updated" : "Service scheduled");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const del = async (s) => {
    if (!window.confirm("Delete this service record?")) return;
    await api.delete(`/services/${s.id}`);
    toast.success("Deleted");
    await load();
  };

  // Dedupe the catalog so each service type shows once, even when the API
  // returns cross-company duplicates (e.g. super-admin has no company scope).
  const uniqueTypes = (() => {
    const seen = new Set();
    const out = [];
    for (const t of serviceTypes) {
      const n = normKey(t.key || t.label);
      if (!n || seen.has(n)) continue;
      seen.add(n);
      out.push(t);
    }
    return out;
  })();

  return (
    <div data-testid="services-page">
      <PageHeader eyebrow="Scheduled maintenance" title="Services" subtitle="Oil changes, tire rotations, inspections — schedule by date or mileage.">
        <Button onClick={openAdd} disabled={trucks.length === 0 && trailers.length === 0} data-testid="add-service-button" className="bg-slate-900 hover:bg-slate-800 rounded-sm">
          <PlusIcon size={16} weight="bold" className="mr-2" /> Add Service
        </Button>
      </PageHeader>

      <div className="p-8">
        {/* Filters */}
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="service-filters">
          <Label className="text-xs uppercase tracking-widest text-slate-600 mr-1">Filter</Label>
          <Select value={filterAsset} onValueChange={setFilterAsset}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="filter-asset"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All assets</SelectItem>
              <SelectItem value="truck">Trucks only</SelectItem>
              <SelectItem value="trailer">Trailers only</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-56 rounded-sm" data-testid="filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All service types</SelectItem>
              {uniqueTypes.map((t) => (
                <SelectItem key={t.id} value={t.key}>{t.label}</SelectItem>
              ))}
              {/* Surface legacy service_type keys that don't match the catalog,
                  collapsing near-duplicates (case/spacing/underscore) into one. */}
              {(() => {
                const catalogNorms = new Set(
                  uniqueTypes.flatMap((t) => [normKey(t.key), normKey(t.label)])
                );
                const seen = new Set();
                const legacy = [];
                for (const s of services) {
                  const raw = s.service_type;
                  if (!raw) continue;
                  const n = normKey(raw);
                  if (!n || catalogNorms.has(n) || seen.has(n)) continue;
                  seen.add(n);
                  legacy.push(raw);
                }
                return legacy.map((k) => (
                  <SelectItem key={`legacy-${normKey(k)}`} value={k} className="capitalize">
                    {k.replace(/_/g, " ")} (legacy)
                  </SelectItem>
                ));
              })()}
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={() => { if (sortBy === "unit") setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortBy("unit"); setSortDir("asc"); } }}
            className={`flex items-center gap-1 px-3 py-1.5 border rounded-sm text-xs font-semibold hover:bg-slate-50 ${sortBy === "unit" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-700 bg-white"}`}
            data-testid="services-sort-unit"
            title="Sort by unit number"
          >
            {sortBy === "unit" && sortDir === "desc" ? <ArrowDownIcon size={12} weight="bold" /> : <ArrowUpIcon size={12} weight="bold" />}
            Unit #
          </button>
          <button
            type="button"
            onClick={() => { if (sortBy === "date") setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortBy("date"); setSortDir("desc"); } }}
            className={`flex items-center gap-1 px-3 py-1.5 border rounded-sm text-xs font-semibold hover:bg-slate-50 ${sortBy === "date" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-700 bg-white"}`}
            data-testid="services-sort-date"
            title="Sort by next due date"
          >
            {sortBy === "date" && sortDir === "desc" ? <ArrowDownIcon size={12} weight="bold" /> : <ArrowUpIcon size={12} weight="bold" />}
            Next Due
          </button>
          <button
            type="button"
            onClick={() => { if (sortBy === "status") setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortBy("status"); setSortDir("asc"); } }}
            className={`flex items-center gap-1 px-3 py-1.5 border rounded-sm text-xs font-semibold hover:bg-slate-50 ${sortBy === "status" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-700 bg-white"}`}
            data-testid="services-sort-status"
            title="Sort by status (overdue & upcoming first)"
          >
            {sortBy === "status" && sortDir === "desc" ? <ArrowDownIcon size={12} weight="bold" /> : <ArrowUpIcon size={12} weight="bold" />}
            Status
          </button>
          {(filterAsset !== "all" || filterType !== "all") && (
            <button
              type="button"
              onClick={() => { setFilterAsset("all"); setFilterType("all"); }}
              className="text-xs text-slate-600 hover:text-slate-900 underline underline-offset-2 ml-1"
              data-testid="clear-filters"
            >clear filters</button>
          )}
          <div className="ml-auto text-xs text-slate-500 font-mono" data-testid="filter-count">
            {(() => {
              const shown = services.filter((s) =>
                (filterAsset === "all" ||
                 (filterAsset === "truck" && !s.trailer_id) ||
                 (filterAsset === "trailer" && !!s.trailer_id)) &&
                (filterType === "all" || s.service_type === filterType)
              ).length;
              return `${shown} of ${services.length}`;
            })()}
          </div>
        </div>

        <div className="bg-white border border-slate-300 rounded-sm">
          {services.length === 0 ? (
            <div className="p-12 text-center">
              <div className="font-mono text-xs text-slate-400 mb-2">/// NO SERVICES SCHEDULED</div>
              <div className="text-sm text-slate-600">Start tracking your maintenance schedule.</div>
            </div>
          ) : (
            <table className="fleet-table">
              <thead>
                <tr>
                  <th>Asset</th>
                  <th>Service</th>
                  <th>Last Date</th>
                  <th>Last Mileage / Hrs</th>
                  <th>Next Due</th>
                  <th>Next Mileage / Hrs</th>
                  <th>Cost</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {services
                  .filter((s) =>
                    (filterAsset === "all" ||
                     (filterAsset === "truck" && !s.trailer_id) ||
                     (filterAsset === "trailer" && !!s.trailer_id)) &&
                    (filterType === "all" || normKey(s.service_type) === normKey(filterType))
                  )
                  .sort((a, b) => {
                    let c;
                    if (sortBy === "date") {
                      const da = a.next_due_date || "";
                      const db = b.next_due_date || "";
                      // Push rows without a due date to the bottom regardless of direction
                      if (!da && !db) c = 0;
                      else if (!da) return 1;
                      else if (!db) return -1;
                      else c = da.localeCompare(db);
                    } else if (sortBy === "status") {
                      const rank = { overdue: 0, upcoming: 1, scheduled: 2, in_progress: 3, completed: 4 };
                      const ra = rank[a.status] ?? 5;
                      const rb = rank[b.status] ?? 5;
                      c = ra - rb;
                    } else {
                      c = String(unitNumberOf(a)).localeCompare(String(unitNumberOf(b)), undefined, { numeric: true, sensitivity: "base" });
                    }
                    return sortDir === "asc" ? c : -c;
                  })
                  .map((s) => {
                  const sb = statusBadge(s.status);
                  const isTrailer = !!s.trailer_id;
                  return (
                    <tr key={s.id} data-testid={`service-list-${s.id}`}>
                      <td className="font-mono font-semibold">{assetLabel(s)}</td>
                      <td className="capitalize font-semibold">
                        {(serviceTypes.find((t) => t.key === s.service_type)?.label) || (s.service_type || "—").replace(/_/g, " ")}
                      </td>
                      <td className="font-mono">{s.last_service_date?.slice(0, 10) || "—"}</td>
                      <td className="font-mono tabular-nums">
                        {isTrailer
                          ? (s.last_service_engine_hours != null ? `${Number(s.last_service_engine_hours).toLocaleString()} hrs` : "—")
                          : (s.last_service_mileage ? Number(s.last_service_mileage).toLocaleString() : "—")}
                      </td>
                      <td className="font-mono">{s.next_due_date?.slice(0, 10) || "—"}</td>
                      <td className="font-mono tabular-nums">
                        {isTrailer
                          ? (s.next_due_engine_hours != null ? `${Number(s.next_due_engine_hours).toLocaleString()} hrs` : "—")
                          : (s.next_due_mileage ? Number(s.next_due_mileage).toLocaleString() : "—")}
                      </td>
                      <td className="font-mono tabular-nums">${Number(s.cost || 0).toLocaleString()}</td>
                      <td><Badge tone={sb.tone}>{sb.label}</Badge></td>
                      <td className="space-x-3">
                        <button onClick={() => openEdit(s)} className="text-blue-600 hover:underline text-xs font-semibold" data-testid={`edit-service-${s.id}`}>Edit</button>
                        <button onClick={() => del(s)} className="text-red-600 hover:underline text-xs font-semibold" data-testid={`del-service-${s.id}`}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="service-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit service" : "Add scheduled service"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700 mb-2 block">Asset *</Label>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, asset_kind: "truck", trailer_id: "", service_type: "oil_change" })}
                  data-testid="service-asset-truck"
                  className={`px-3 py-2 border-2 rounded-sm text-sm font-semibold transition-colors duration-150 ${form.asset_kind === "truck" ? "border-blue-600 bg-blue-50" : "border-slate-300 hover:bg-slate-50"}`}
                >
                  Truck
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, asset_kind: "trailer", truck_id: "", service_type: "reefer_oil_change" })}
                  data-testid="service-asset-trailer"
                  className={`px-3 py-2 border-2 rounded-sm text-sm font-semibold transition-colors duration-150 ${form.asset_kind === "trailer" ? "border-blue-600 bg-blue-50" : "border-slate-300 hover:bg-slate-50"}`}
                >
                  Trailer
                </button>
              </div>
              {form.asset_kind === "truck" ? (
                <Select value={form.truck_id} onValueChange={(v) => setForm({ ...form, truck_id: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="service-truck"><SelectValue placeholder="Select truck" /></SelectTrigger>
                  <SelectContent>
                    {sortByUnit(trucks).map((t) => <SelectItem key={t.id} value={t.id}>{t.unit_number} · {t.make} {t.model}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Select value={form.trailer_id} onValueChange={(v) => setForm({ ...form, trailer_id: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="service-trailer"><SelectValue placeholder="Select trailer" /></SelectTrigger>
                  <SelectContent>
                    {sortByUnit(trailers).map((t) => <SelectItem key={t.id} value={t.id}>{t.unit_number} · {t.trailer_type.replace(/_/g, " ")} · {t.make}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Service Type</Label>
              <Select value={form.service_type || ""} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="service-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {uniqueTypes
                    .filter((t) => t.active !== false)
                    .map((t) => (
                      <SelectItem key={t.id} value={t.key}>{t.label}</SelectItem>
                    ))}
                  {/* Preserve unknown legacy values so nothing goes blank on edit */}
                  {form.service_type && !uniqueTypes.some((t) => normKey(t.key) === normKey(form.service_type)) && (
                    <SelectItem value={form.service_type} className="capitalize">
                      {form.service_type.replace(/_/g, " ")} (legacy)
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="service-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Last service date</Label>
              <Input type="date" value={form.last_service_date} onChange={(e) => setForm({ ...form, last_service_date: e.target.value })} className="mt-1.5 rounded-sm" data-testid="service-last-date" />
            </div>
            {form.asset_kind === "trailer" ? (
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Last service engine hours</Label>
                <Input type="number" step="0.1" value={form.last_service_engine_hours} onChange={(e) => setForm({ ...form, last_service_engine_hours: e.target.value })} className="mt-1.5 rounded-sm" data-testid="service-last-hours" />
              </div>
            ) : (
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Last service mileage</Label>
                <div className="mt-1.5">
                  <MileageInput value={form.last_service_mileage} onChange={(v) => setForm({ ...form, last_service_mileage: v })} data-testid="service-last-mileage" />
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Next due date</Label>
              <Input type="date" value={form.next_due_date} onChange={(e) => setForm({ ...form, next_due_date: e.target.value })} className="mt-1.5 rounded-sm" data-testid="service-next-date" />
              {AUTO_INTERVAL_DAYS[form.service_type] && form.last_service_date && (
                <div className="text-[10px] text-blue-700 mt-1 font-mono uppercase tracking-wider" data-testid="auto-fill-hint">
                  Auto-scheduled {form.service_type === "annual_safety" ? "1 year" : "4 months"} after last service
                </div>
              )}
            </div>
            {form.asset_kind === "trailer" ? (
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Next due engine hours</Label>
                <Input type="number" step="0.1" value={form.next_due_engine_hours} onChange={(e) => setForm({ ...form, next_due_engine_hours: e.target.value })} className="mt-1.5 rounded-sm" data-testid="service-next-hours" />
              </div>
            ) : (
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Next due mileage</Label>
                <div className="mt-1.5">
                  <MileageInput value={form.next_due_mileage} onChange={(v) => setForm({ ...form, next_due_mileage: v })} data-testid="service-next-mileage" />
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Cost ($)</Label>
              <Input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="mt-1.5 rounded-sm" data-testid="service-cost" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Performed by</Label>
              <Input value={form.performed_by} onChange={(e) => setForm({ ...form, performed_by: e.target.value })} className="mt-1.5 rounded-sm" data-testid="service-by" />
            </div>

            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 rounded-sm" data-testid="service-notes" />
            </div>

            <DialogFooter className="col-span-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="service-save">{editing ? "Update" : "Save"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
